import axios, { type AxiosError, type AxiosInstance } from 'axios';

/**
 * Shared axios client for talking to the Laravel admin API.
 *
 * Uses Sanctum SPA cookie auth:
 *   - withCredentials so the session cookie travels on every request
 *   - withXSRFToken so axios auto-reads the XSRF-TOKEN cookie and sends it as
 *     the X-XSRF-TOKEN header (Laravel verifies this for state-changing methods)
 *
 * All paths are RELATIVE on purpose. In dev the SPA is served by Vite at
 * admin.climate.local:5173 and Vite proxies /api and /sanctum to the Laravel
 * container — same-origin from the browser's POV, so no CORS. In prod the
 * SPA's nginx will be configured the same way (or the API will be on the
 * same root domain). If you ever do need a cross-origin baseURL, add it to
 * CORS allowed_origins on the Laravel side first.
 */
function createApiClient(): AxiosInstance {
    const client = axios.create({
        baseURL: '',
        withCredentials: true,
        withXSRFToken: true,
        headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
    });

    // Single retry on 419 (session/CSRF token expired) — fetch a fresh
    // XSRF-TOKEN cookie and re-issue the original request.
    client.interceptors.response.use(undefined, async (error: AxiosError) => {
        const status = error.response?.status;
        const config = error.config as (typeof error.config & { __retried?: boolean }) | undefined;

        if (status === 419 && config && !config.__retried) {
            config.__retried = true;
            await client.get('/sanctum/csrf-cookie');
            return client.request(config);
        }

        return Promise.reject(error);
    });

    return client;
}

export const api = createApiClient();

/**
 * Lazily prime the CSRF cookie. Sanctum issues an XSRF-TOKEN cookie on the
 * first GET to /sanctum/csrf-cookie; the SPA must call this before any
 * state-changing request (login, logout, POST, ...).
 */
export async function ensureCsrfCookie(): Promise<void> {
    await api.get('/sanctum/csrf-cookie');
}

/**
 * Assert that a response payload really is the list it claims to be.
 *
 * Pages read these straight into `v-for` and `.length`, so an undefined one
 * throws during render and blanks the page (ticket #2440). Defaulting to an
 * empty array would stop the crash, but it would also make a broken API look
 * like an empty database — "No frameworks available" sends someone hunting for
 * missing data instead of reporting the fault. Throwing instead lands the page
 * on the error state it already has.
 */
export function expectList<T>(value: T[] | null | undefined, what: string): T[] {
    if (!Array.isArray(value)) {
        throw new Error(`The server did not return a list of ${what}.`);
    }

    return value;
}
